import { useEffect, useState } from "react";
import { LayoutGrid, Rows3 } from "lucide-react";

export type ViewMode = "card" | "row";

const STORAGE_PREFIX = "aivrmanager.viewMode.";

/**
 * Read a persisted view mode for `key` from localStorage; falls back to
 * `fallback` when nothing is stored or when localStorage is unavailable
 * (e.g. private mode quirks).
 */
export function loadViewMode(key: string, fallback: ViewMode = "card"): ViewMode {
  try {
    const v = window.localStorage.getItem(STORAGE_PREFIX + key);
    return v === "row" || v === "card" ? v : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Persist `mode` for `key`, swallowing any storage errors silently —
 * the toggle should never throw at the user even if storage is denied.
 */
export function saveViewMode(key: string, mode: ViewMode): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, mode);
  } catch {
    /* ignore */
  }
}

/**
 * Generic persisted choice (same storage prefix as the view mode). Only
 * values in `allowed` are accepted back from storage, so a stale or
 * tampered entry can never produce an unknown mode.
 */
export function loadStoredChoice<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = window.localStorage.getItem(STORAGE_PREFIX + key);
    return (allowed as readonly string[]).includes(v ?? "") ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

export function saveStoredChoice(key: string, value: string): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, value);
  } catch {
    /* ignore */
  }
}

/**
 * Tracks whether `ref`'s width is wide enough to fit at least two
 * columns of `minColWidth` (with `gap` between them). Useful for
 * auto-hiding the view toggle on narrow screens — when only one card
 * fits per row anyway, the toggle has nothing to switch between.
 */
export function useFitsMultipleColumns(
  ref: React.RefObject<HTMLElement>,
  minColWidth: number,
  gap = 16,
): boolean {
  const [fits, setFits] = useState(true);
  useEffect(() => {
    if (!ref.current) return;
    const node = ref.current;
    const measure = () => {
      const w = node.clientWidth;
      setFits(w >= minColWidth * 2 + gap);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [ref, minColWidth, gap]);
  return fits;
}

/**
 * Two-button card/row view toggle. Wrap your overview's grid (or list)
 * in a parent that drives `value` and conditionally render this toggle
 * only when there's enough horizontal space for >1 column — see
 * `useFitsMultipleColumns`.
 */
export function ViewToggle({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (next: ViewMode) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="View"
      style={{
        display: "inline-flex",
        background: "white",
        border: "1px solid var(--portal-border)",
        borderRadius: 999,
        padding: 2,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      <ToggleButton
        active={value === "card"}
        onClick={() => onChange("card")}
        label="Cards"
        ariaLabel="Show as cards"
      >
        <LayoutGrid size={16} />
      </ToggleButton>
      <ToggleButton
        active={value === "row"}
        onClick={() => onChange("row")}
        label="Rows"
        ariaLabel="Show as rows"
      >
        <Rows3 size={16} />
      </ToggleButton>
    </div>
  );
}

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  ariaLabel: string;
  icon: React.ReactNode;
}

/**
 * N-option variant of {@link ViewToggle} with the same pill styling —
 * e.g. the Grid / Cards / Auto layout choice on the data-sheet records.
 */
export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  ariaLabel = "View",
}: {
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (next: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: "inline-flex",
        background: "white",
        border: "1px solid var(--portal-border)",
        borderRadius: 999,
        padding: 2,
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      {options.map((o) => (
        <ToggleButton
          key={o.value}
          active={value === o.value}
          onClick={() => onChange(o.value)}
          label={o.label}
          ariaLabel={o.ariaLabel}
        >
          {o.icon}
        </ToggleButton>
      ))}
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  label,
  ariaLabel,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        border: 0,
        background: active ? "var(--portal-primary)" : "transparent",
        color: active ? "white" : "var(--portal-muted)",
        borderRadius: 999,
        fontSize: "0.85rem",
        fontWeight: 600,
        cursor: "pointer",
        transition: "background 0.15s ease, color 0.15s ease",
      }}
    >
      {children}
      <span aria-hidden="true">{label}</span>
    </button>
  );
}
